"use client";
import React from "react";

const getScrollbarWidth = () => {
  if (!("document" in globalThis)) {
    return 0;
  }
  const body = document.body;
  const scrollDiv = document.createElement("div");
  scrollDiv.style.height = "50px";
  scrollDiv.style.overflow = "scroll";
  scrollDiv.style.position = "absolute";
  scrollDiv.style.top = "-100px";
  scrollDiv.style.width = "50px";
  body.appendChild(scrollDiv);
  const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  body.removeChild(scrollDiv);
  return scrollbarWidth;
};

interface OverlayContext {
  mountOverlay: () => number;
  unmountOverlay: (arg: number) => void;
  getOverlayZIndex: (arg: number) => number;
}

export const OverlayContext = React.createContext<OverlayContext>({
  mountOverlay: () => 0,
  unmountOverlay: () => {},
  getOverlayZIndex: () => 1
});

export const OverlayContextProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const [overlayStack, setOverlayStack] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (overlayStack.length > 0) {
      if (overlayStack.length === 1) {
        document.body.style.paddingInlineEnd = `${getScrollbarWidth()}px`;
        document.body.style.overflow = "hidden";
      }
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingInlineEnd = "";
    }
  }, [overlayStack]);

  const mountOverlay = React.useCallback(() => {
    const newOverlayId = Date.now();
    setOverlayStack((stack) => [...stack, newOverlayId]);
    return newOverlayId;
  }, []);

  const unmountOverlay = React.useCallback((overlayId: number) => {
    setOverlayStack((stack) => stack.filter((id) => id !== overlayId));
  }, []);

  const getOverlayZIndex = React.useCallback(
    (overlayId: number) => {
      return overlayStack.indexOf(overlayId) + 1;
    },
    [overlayStack]
  );
  return (
    <OverlayContext.Provider
      value={{ mountOverlay, unmountOverlay, getOverlayZIndex }}
    >
      {children}
    </OverlayContext.Provider>
  );
};

export const useOverlay = () => {
  return React.useContext(OverlayContext);
};
